<template>
  <div class="panel ask-allen" :class="{ bare }">
    <!-- Header hidden in `bare` mode — the docked shell already shows the
         Ask Allen title/avatar, so this would be a duplicate. -->
    <div v-if="!bare" class="ask-head">
      <span class="ask-avatar"><i class="bi bi-robot"></i></span>
      <div>
        <div class="ask-head-title">{{ t('askAllen.title') }}</div>
        <div class="ask-head-sub">
          {{ mode === 'demo' ? t('askAllen.subDemo') : t('askAllen.subLive') }}
        </div>
      </div>
    </div>

    <div v-if="thread.length || asking" ref="threadEl" class="ask-thread">
      <template v-for="(t, i) in thread" :key="i">
        <div class="ask-bubble ask-user">{{ t.q }}</div>
        <div class="ask-allen-row">
          <span class="ask-avatar sm"><i class="bi bi-robot"></i></span>
          <div class="ask-bubble ask-allen-msg">{{ t.a }}</div>
        </div>
      </template>
      <template v-if="asking">
        <div class="ask-bubble ask-user">{{ pendingQ }}</div>
        <div class="ask-allen-row">
          <span class="ask-avatar sm"><i class="bi bi-robot"></i></span>
          <div class="ask-bubble ask-allen-msg ask-typing"><span></span><span></span><span></span></div>
        </div>
      </template>
    </div>

    <div v-if="!asking && visibleChips.length" class="ask-chips">
      <span class="ask-chips-label">{{ thread.length ? t('askAllen.tryAnother') : t('askAllen.tryAsking') }}</span>
      <button v-for="(q, i) in visibleChips" :key="i" class="ask-chip" @click="submitAsk(q)">{{ q }}</button>
    </div>

    <p v-if="askError" class="ask-error">{{ askError }}</p>

    <form class="ask-form" @submit.prevent="submitAsk()">
      <input v-model="askInput" :disabled="asking" type="text" class="ask-input" maxlength="500"
             :placeholder="mode === 'demo' ? t('askAllen.placeholderDemo') : t('askAllen.placeholderLive')" />
      <button type="submit" class="ask-send" :disabled="asking || !askInput.trim()" :aria-label="t('askAllen.send')">
        <i class="bi bi-arrow-up"></i>
      </button>
    </form>

    <p v-if="mode === 'demo'" class="ask-demo-cta">
      <i class="bi bi-stars"></i> <i18n-t keypath="askAllen.demoCta" scope="global"><template #link><a :href="ctaHref">{{ ctaLabel || t('askAllen.contactCta') }}</a></template></i18n-t>
    </p>
    <p v-else class="ask-hint">{{ t('askAllen.hint') }}</p>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCompanyStore } from '@/stores/CompanyStore'
import { allenThreadFor } from '@/composables/allenThread'

const props = defineProps({
  mode: { type: String, default: 'live' },              // 'live' | 'demo'
  bare: { type: Boolean, default: false },              // inside AllenDock: no header/border, fill height
  suggestedQuestions: { type: Array, default: () => [] },
  cannedAnswers: { type: Array, default: () => [] },     // demo: [{keywords:[], answer}]
  ctaLabel: { type: String, default: '' },              // '' -> "Contact Storalytic" in the reader's language
  ctaHref: { type: String, default: 'https://storalytic.io' },
})

// Only used in live mode — demo never calls it, so the open demo can't hit OpenAI.
const companyStore = useCompanyStore()
const { t } = useI18n()

const askInput = ref('')
const asking = ref(false)
const askError = ref('')
// Session-persistent thread (survives close/reopen + reload; clears on tab close).
const thread = computed(() => allenThreadFor(props.mode))
const pendingQ = ref('')
const threadEl = ref(null)

// Suggested chips, minus any already asked (a clicked chip shouldn't reappear).
const visibleChips = computed(() =>
  (props.suggestedQuestions || []).filter((q) => !thread.value.some((t) => t.q === q))
)

function demoReply(q) {
  const low = q.toLowerCase()
  const hit = props.cannedAnswers.find((e) => (e.keywords || []).some((k) => low.includes(k)))
  if (hit) return hit.answer
  // Off-topic / unscripted: answer warmly, then point to a question that always works.
  const s = props.suggestedQuestions[0] || t('askAllen.prompts.general.0')
  return t('askAllen.demoFallback', { question: s })
}

async function scrollThread() {
  await nextTick()
  if (threadEl.value) threadEl.value.scrollTop = threadEl.value.scrollHeight
}

async function submitAsk(preset) {
  const q = (preset || askInput.value).trim()
  if (!q || asking.value) return
  askError.value = ''
  askInput.value = ''
  pendingQ.value = q
  asking.value = true
  scrollThread()
  try {
    let a
    if (props.mode === 'demo') {
      await new Promise((r) => setTimeout(r, 1000))      // mimic "thinking", no network
      a = demoReply(q)
    } else {
      a = await companyStore.askAllen(q, 'past_30_days', thread.value.slice(-6))
    }
    thread.value.push({ q, a })
  } catch (err) {
    askError.value = err.message || t('askAllen.error')
    askInput.value = q
  } finally {
    pendingQ.value = ''
    asking.value = false
    scrollThread()
  }
}
</script>

<style scoped>
.panel {
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.ask-allen { padding: 1.15rem 1.3rem; margin-top: var(--gap, 12px); }
/* Bare mode (inside AllenDock): no border/shadow/header — the dock shell provides
   those — and fill the height so the input sits at the bottom (no gap below). */
.ask-allen.bare {
  border: 0; box-shadow: none; border-radius: 0; padding: 0; margin-top: 0;
  height: 100%; display: flex; flex-direction: column; background: transparent;
}
.ask-allen.bare .ask-thread { flex: 1 1 auto; max-height: none; }
.ask-head { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.95rem; }
.ask-head-title { font-weight: 700; color: #1d2b3a; font-size: 1rem; line-height: 1.1; }
.ask-head-sub { font-size: 0.78rem; color: var(--stora-muted, #6b7a86); margin-top: 0.1rem; }
.ask-avatar {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff; font-size: 1rem; box-shadow: 0 3px 8px rgba(79, 126, 179, 0.3);
}
.ask-avatar.sm { width: 26px; height: 26px; font-size: 0.8rem; margin-top: 0.15rem; box-shadow: none; }

.ask-thread {
  display: flex; flex-direction: column; gap: 0.7rem;
  max-height: 360px; overflow-y: auto; padding: 0.2rem 0.15rem 0.5rem; margin-bottom: 0.8rem;
}
.ask-bubble { max-width: 82%; padding: 0.55rem 0.85rem; font-size: 0.93rem; line-height: 1.5; border-radius: 14px; }
.ask-user { align-self: flex-end; background: var(--stora-accent, #4F7EB3); color: #fff; border-bottom-right-radius: 4px; }
.ask-allen-row { display: flex; align-items: flex-start; gap: 0.5rem; align-self: flex-start; max-width: 92%; }
.ask-allen-msg {
  background: #f1f5fa; color: #243b53; border: 1px solid var(--stora-border, #e6edf3); border-bottom-left-radius: 4px;
}
.ask-typing { display: inline-flex; gap: 4px; align-items: center; padding: 0.7rem 0.85rem; }
.ask-typing span {
  width: 6px; height: 6px; border-radius: 50%; background: #9bb3cc; animation: ask-bounce 1.2s infinite ease-in-out;
}
.ask-typing span:nth-child(2) { animation-delay: 0.15s; }
.ask-typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes ask-bounce { 0%, 80%, 100% { opacity: 0.35; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }

.ask-chips { display: flex; flex-wrap: wrap; align-items: center; gap: 0.45rem; margin-bottom: 0.85rem; }
.ask-chips-label { font-size: 0.8rem; color: var(--stora-muted, #6b7a86); margin-right: 0.15rem; }
.ask-chip {
  border: 1px solid #dbe7f5; background: #eef5ff; color: var(--stora-accent, #4F7EB3);
  font-size: 0.82rem; font-weight: 500; padding: 0.4rem 0.8rem; border-radius: 999px;
  cursor: pointer; transition: background 0.15s ease, transform 0.15s ease;
}
.ask-chip:hover { background: #e0edfb; transform: translateY(-1px); }

.ask-error { color: #B44A55; font-size: 0.86rem; margin: 0 0 0.6rem; }
.ask-form { display: flex; gap: 0.5rem; align-items: center; }
.ask-input {
  flex: 1; border: 1px solid var(--stora-border, #e6edf3); border-radius: 999px;
  padding: 0.62rem 1rem; font-size: 0.95rem; color: #1d2b3a; outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ask-input:focus { border-color: var(--stora-accent, #4F7EB3); box-shadow: 0 0 0 3px rgba(79, 126, 179, 0.12); }
.ask-input:disabled { background: #f4f7fb; }
.ask-send {
  flex: 0 0 auto; width: 42px; height: 42px; border: none; border-radius: 50%; cursor: pointer;
  background: var(--stora-accent, #4F7EB3); color: #fff; font-size: 1.15rem;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s ease;
}
.ask-send:hover:not(:disabled) { background: #3f6c9e; }
.ask-send:disabled { opacity: 0.45; cursor: default; }
.ask-hint { margin: 0.6rem 0 0; font-size: 0.74rem; color: #9aa7b1; }
.ask-demo-cta { margin: 0.6rem 0 0; font-size: 0.8rem; color: var(--stora-muted, #6b7a86); }
.ask-demo-cta i { color: var(--stora-accent, #4F7EB3); }
.ask-demo-cta a { color: var(--stora-accent, #4F7EB3); font-weight: 600; text-decoration: none; }
.ask-demo-cta a:hover { text-decoration: underline; }
</style>
